import React, { useState } from "react";
import { addChildLocation } from "../api";


const DAYS_OF_WEEK = [
    "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"
];

const EVENT_TYPE = { id: 3, name: "WEEKLY" };
const EVENT_STATUS_TYPE = { id: 1, name: "OPEN" };

const initialState = {
    locationName: "",
    locationType: "Office",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
    appointmentUrl: "",
    contactName: "",
    phoneNumber: "",
    email: "",
    fax: "",
    maxCapacity: 10,
    appointmentDuration: 120,
    events: DAYS_OF_WEEK.map(day => ({
        dayOfWeek: day,
        startTime: "09:00:00",
        endTime: "17:00:00",
        isWholeDay: false,
        isCancelled: false,
        startDate: "",
        endDate: "",
        eventDescription: "",
    })),
};

const validateEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const validatePhone = (phone) =>
    /^\+?[0-9]{10,15}$/.test(phone);

export default function AddLocationModal({ locationRef, token, onClose, onSuccess }) {
    const [form, setForm] = useState(initialState);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        if (name === "phoneNumber" || name === "email") setError("");
    };

    const handleEventTimeChange = (idx, field, value) => {
        setForm(prev => ({
            ...prev,
            events: prev.events.map((ev, i) =>
                i === idx ? { ...ev, [field]: value } : ev
            ),
        }));
    };

    const handleEventDescChange = (idx, value) => {
        setForm(prev => ({
            ...prev,
            events: prev.events.map((ev, i) =>
                i === idx ? { ...ev, eventDescription: value } : ev
            ),
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateEmail(form.email)) {
            setError("Invalid email format");
            return;
        }
        if (!validatePhone(form.phoneNumber)) {
            setError("Invalid phone number. Please enter a valid phone number (10-15 digits)");
            return;
        }
        setError("");
        setLoading(true);
        try {
            const events = form.events.map(ev => ({
                eventDescription: ev.eventDescription || `Weekly event for ${ev.dayOfWeek}`,
                eventType: EVENT_TYPE,
                eventStatusType: EVENT_STATUS_TYPE,
                startDate: ev.startDate || new Date().toISOString().slice(0, 10),
                endDate: ev.endDate || null,
                startTime: ev.startTime,
                endTime: ev.endTime,
                dayOfWeek: ev.dayOfWeek,
                intervalCount: 1,
                dayOfMonth: null,
                monthOfYear: null,
                isWholeDay: ev.isWholeDay,
                isCancelled: ev.isCancelled,
            }));

            const payload = {
                ...form,
                events,
                maxCapacity: Number(form.maxCapacity) || 10,
                appointmentDuration: Number(form.appointmentDuration) || 120,
            };
            delete payload.calendar; // Remove if present
            await addChildLocation(locationRef, token, payload);
            onSuccess?.();
            onClose();
        } catch {
            setError("Failed to add location");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="w-full h-full min-h-0 flex flex-col transition-opacity duration-500">
            <div className="flex-1 overflow-y-auto bg-white w-full p-fluid-1">
                <div className="bg-white rounded-[clamp(0.75rem,1vw,1.5rem)] shadow-fluid-card p-fluid-2">


                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-8">
                        {/* Basic Information Section */}
                        <div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <label className="block font-medium mb-1">Location Name *</label>
                                    <input
                                        type="text"
                                        name="locationName"
                                        value={form.locationName}
                                        onChange={handleChange}
                                        required
                                        className="border rounded-md p-2 w-full"
                                        placeholder="Enter location name"
                                    />
                                </div>
                                <div>
                                    <label className="block font-medium mb-1">Location Type *</label>
                                    <select
                                        name="locationType"
                                        value={form.locationType}
                                        onChange={handleChange}
                                        required
                                        className="border rounded-md p-2 w-full"
                                    >
                                        <option value="Office">Office</option>
                                        <option value="Warehouse">Warehouse</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block font-medium mb-1">Country *</label>
                                    <input
                                        type="text"
                                        name="country"
                                        value={form.country}
                                        onChange={handleChange}
                                        required
                                        className="border rounded-md p-2 w-full"
                                        placeholder="Enter country"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Address Information Section */}
                        <div>
                            <h3 className="text-fluid-xl font-semibold mb-4 text-gray-800">Address Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="md:col-span-2">
                                    <label className="block font-medium mb-1">Address Line 1 *</label>
                                    <input
                                        type="text"
                                        name="addressLine1"
                                        value={form.addressLine1}
                                        onChange={handleChange}
                                        required
                                        className="border rounded-md p-2 w-full"
                                        placeholder="Enter address line 1"
                                    />
                                </div>
                                <div>
                                    <label className="block font-medium mb-1">Address Line 2</label>
                                    <input
                                        type="text"
                                        name="addressLine2"
                                        value={form.addressLine2}
                                        onChange={handleChange}
                                        className="border rounded-md p-2 w-full"
                                        placeholder="Enter address line 2"
                                    />
                                </div>
                                <div>
                                    <label className="block font-medium mb-1">City *</label>
                                    <input
                                        type="text"
                                        name="city"
                                        value={form.city}
                                        onChange={handleChange}
                                        required
                                        className="border rounded-md p-2 w-full"
                                        placeholder="Enter city"
                                    />
                                </div>
                                <div>
                                    <label className="block font-medium mb-1">State *</label>
                                    <input
                                        type="text"
                                        name="state"
                                        value={form.state}
                                        onChange={handleChange}
                                        required
                                        className="border rounded-md p-2 w-full"
                                        placeholder="Enter state"
                                    />
                                </div>
                                <div>
                                    <label className="block font-medium mb-1">Zip Code *</label>
                                    <input
                                        type="text"
                                        name="zipCode"
                                        value={form.zipCode}
                                        onChange={handleChange}
                                        required
                                        className="border rounded-md p-2 w-full"
                                        placeholder="Enter zip code"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Contact Information Section */}
                        <div>
                            <h3 className="text-fluid-xl font-semibold mb-4 text-gray-800">Contact Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <label className="block font-medium mb-1">Contact Name *</label>
                                    <input
                                        type="text"
                                        name="contactName"
                                        value={form.contactName}
                                        onChange={handleChange}
                                        required
                                        className="border rounded-md p-2 w-full"
                                        placeholder="Enter contact name"
                                    />
                                </div>
                                <div>
                                    <label className="block font-medium mb-1">Phone Number *</label>
                                    <input
                                        type="text"
                                        name="phoneNumber"
                                        value={form.phoneNumber}
                                        onChange={handleChange}
                                        required
                                        className={`border rounded-md p-2 w-full ${error && form.phoneNumber ? "border-red-500" : ""}`}
                                        placeholder="e.g. +93-3681178825"
                                    />
                                </div>
                                <div>
                                    <label className="block font-medium mb-1">Email *</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={form.email}
                                        onChange={handleChange}
                                        required
                                        className={`border rounded-md p-2 w-full ${error && form.email ? "border-red-500" : ""}`}
                                        placeholder="Enter email"
                                    />
                                </div>
                                <div>
                                    <label className="block font-medium mb-1">Fax</label>
                                    <input
                                        type="text"
                                        name="fax"
                                        value={form.fax}
                                        onChange={handleChange}
                                        className="border rounded-md p-2 w-full"
                                        placeholder="Enter fax"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block font-medium mb-1">Appointment URL</label>
                                    <input
                                        type="text"
                                        name="appointmentUrl"
                                        value={form.appointmentUrl}
                                        onChange={handleChange}
                                        className="border rounded-md p-2 w-full"
                                        placeholder="Enter appointment URL"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Calendar Settings Section */}
                        <div>
                            <h3 className="text-fluid-xl font-semibold mb-4 text-gray-800">Calendar Settings</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <label className="block font-medium mb-1">Max Capacity</label>
                                    <input
                                        type="number"
                                        name="maxCapacity"
                                        value={form.maxCapacity}
                                        onChange={handleChange}
                                        className="border rounded-md p-2 w-full"
                                        placeholder="Max Capacity"
                                        min={1}
                                    />
                                </div>
                                <div>
                                    <label className="block font-medium mb-1">Appointment Duration (minutes)</label>
                                    <input
                                        type="number"
                                        name="appointmentDuration"
                                        value={form.appointmentDuration}
                                        onChange={handleChange}
                                        className="border rounded-md p-2 w-full"
                                        placeholder="Appointment Duration"
                                        min={1}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Weekly Events Section */}
                        <div>
                            <h3 className="text-fluid-xl font-semibold mb-4 text-gray-800">Weekly Event Times</h3>
                            <div className="flex flex-col gap-3">
                                {(form.events || []).map((ev, idx) => (
                                    <div key={ev.dayOfWeek} className="flex items-center gap-4">
                                        <label className="w-24 text-fluid font-semibold">
                                            {ev.dayOfWeek.charAt(0) + ev.dayOfWeek.slice(1).toLowerCase()}
                                        </label>
                                        <input
                                            className="w-[10rem] px-2 py-1 border border-gray-300 rounded text-center"
                                            type="time"
                                            value={ev.startTime.slice(0,5)}
                                            onChange={e => handleEventTimeChange(idx, "startTime", e.target.value + ":00")}
                                        />
                                        <span className="mx-1">—</span>
                                        <input
                                            className="w-[10rem] px-2 py-1 border border-gray-300 rounded text-center"
                                            type="time"
                                            value={ev.endTime.slice(0,5)}
                                            onChange={e => handleEventTimeChange(idx, "endTime", e.target.value + ":00")}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Error message */}
                        {error && (
                            <div className="text-red-600 text-sm flex items-center bg-red-50 p-3 rounded-md">
                                <span className="mr-2">&#9888;</span>
                                {error}
                            </div>
                        )}

                        {/* Buttons */}
                        <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-6 py-2 bg-[#1e2a74] text-white hover:bg-[#18225c] rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? "Adding..." : "Add Location"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}